import { useEffect, useRef, useState } from "react";
import { CONFETTI_DURATION_MS, CONFETTI_COUNT, THUMBS_COUNT } from "../../constants/animations";

// ── Tunable constants ──────────────────────────────────────────────────────
const NUM_GUARDS          = 9;     // total guards spawned — more = feels chaotic, fewer = more breathing room

// ── Speeds (px/s) ──────────────────────────────────────────────────────────
// The outcome (caught vs escape) is PRE-DECIDED at run start (10% escape).
// Speed caps enforce the result: lucky streaker gets ESCAPE_SPEED_LUCKY (290) which
// outpaces GUARD_SPEED (195). Normal run gets ESCAPE_SPEED (120) — guards win.
// So tweaking GUARD_SPEED only affects how QUICKLY the chase closes, not who wins.
const STREAKER_SPEED      = 100;   // px/s cap while looping (pre-break) — lower = lazier circles
const ESCAPE_SPEED        = 90;   // px/s cap during normal escape attempt — guards (195) still win
const GUARD_SPEED         = 280;   // px/s guard chase cap — raise to make guards feel relentless;
                                   //   lower to give streaker more hang time before catch
const GUARD_PILE_SPEED    = 260;   // px/s guards converge on catch point — higher = faster dog-pile
const ESCAPE_SPEED_LUCKY  = 290;   // px/s cap for the lucky 10% escape run — must stay > GUARD_SPEED

// ── Motion feel ────────────────────────────────────────────────────────────
const LOOP_TURN_RATE      = 2.2;   // rad/s angular steering during loop phase — higher = tighter spirals
const LOOP_PHASE_MS       = 2500;  // ms of looping before making a break — raise for more crowd tease
const ESCAPE_BIAS         = 190;   // force pulling streaker toward the exit edge — higher = beelines faster
const STREAKER_ACCEL      = 55;    // px/s² — how snappily the streaker reaches max speed (lower = floatier)
const GUARD_ACCEL         = 160;   // px/s² — guard responsiveness to direction changes (lower = sluggish turns)

// ── Collision / boundary forces ────────────────────────────────────────────
const OBSTACLE_REPEL      = 1300;  // repulsion strength from [data-streaker-obstacle] elements
const GUARD_REPEL         = 580;   // streaker dodge force when a guard gets within 115px
const OBSTACLE_BUFFER     = 10;    // extra px added to obstacle radius for collision padding
const BOUNDARY_REPEL      = 700;   // force pushing streaker away from field edges during loop phase
const BOUNDARY_PAD        = 28;    // px from edge where boundary force starts kicking in

// ── Game logic ─────────────────────────────────────────────────────────────
const CATCH_DIST          = 18;    // px — guard must be within this distance to register a catch;
                                   //   lower = harder to catch (streaker is slipperier)
const PILE_HOLD_MS        = 1500;  // ms the pile-on is held on screen before clearing
const MAX_RUN_MS          = 8000;  // hard timeout — if still running at 8s, streaker auto-escapes

// CONFETTI_DURATION_MS, CONFETTI_COUNT, THUMBS_COUNT imported from ../../constants/animations

// ── Fun ideas to try ───────────────────────────────────────────────────────
// • Raise LOOP_PHASE_MS to 4000 + lower LOOP_TURN_RATE to 1.5 → lazier, longer crowd tease
// • Lower CATCH_DIST to 10 → streaker narrowly dodges and gets more screen time
// • Raise NUM_GUARDS to 12 + lower GUARD_SPEED to 160 → swarm that's slow to close
// • Try ESCAPE_SPEED_LUCKY = 340 vs GUARD_SPEED = 220 → faster lucky runs look more dramatic

// ── [BUTT-DOT] Calibration — to revert, delete this block + the 3 marked
//    sections below (refs, tick DOM writes, JSX divs). ─────────────────────
const BUTT_DOT_SCALE     = 0.7;  // size relative to the 14px main dot
const BUTT_DOT_PROTRUDE  = 1.5;     // px the dots stick out past the trailing edge
const BUTT_DOT_SPREAD    = 2.5;   // px lateral offset from the movement axis (each side)
const _MAIN_R            = 7;     // half of 14px streaker dot — derived, don't touch
const BUTT_DOT_SIZE_PX   = Math.round(14 * BUTT_DOT_SCALE);        // 8 px
const BUTT_DOT_RADIUS    = BUTT_DOT_SIZE_PX / 2;                   // 4 px
const BUTT_DOT_BACK_DIST = _MAIN_R - BUTT_DOT_RADIUS + BUTT_DOT_PROTRUDE; // 5 px
// Jiggle is speed-driven: freq ∝ speed, amp ∝ (1 − speed). At zero speed both dots
// land at scale 1.0 (same size). Easy to re-tune — only touch these four lines.
const BUTT_JIGGLE_FREQ_SLOW = 2.0;  // Hz floor when barely moving (> dead zone)
const BUTT_JIGGLE_FREQ_MAX  = 5.0;  // Hz at BUTT_JIGGLE_SPEED_REF and above
const BUTT_JIGGLE_AMP_SLOW  = 0.04; // scale-down at near-zero speed (barely visible)
const BUTT_JIGGLE_AMP_FAST  = 0.30; // scale-down at full speed (noticeable waggle)
const BUTT_JIGGLE_SPEED_REF = 100;  // px/s treated as "full speed" for normalization
// ─────────────────────────────────────────────────────────────────────────

const FIELD_SELECTOR = '[data-testid="diamond-field"]';

function randInt(min, max) { return min + Math.floor(Math.random() * (max - min + 1)); }
function randRange(min, max) { return min + Math.random() * (max - min); }
function clampMag(vx, vy, max) {
  const mag = Math.hypot(vx, vy);
  if (mag <= max) return [vx, vy];
  return [(vx / mag) * max, (vy / mag) * max];
}

function snapshotObstacles(fieldRect) {
  const els = typeof document === "undefined" ? [] : document.querySelectorAll("[data-streaker-obstacle]");
  const out = [];
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    const cx = r.left + r.width / 2 - fieldRect.left;
    const cy = r.top  + r.height / 2 - fieldRect.top;
    if (cx < -40 || cy < -40 || cx > fieldRect.width + 40 || cy > fieldRect.height + 40) continue;
    out.push({ cx, cy, radius: Math.max(r.width, r.height) / 2 + OBSTACLE_BUFFER });
  }
  return out;
}

// ── Confetti particle ──────────────────────────────────────────────────────
function ConfettiParticle({ emoji, x, startY, delay, duration, size, dir }) {
  return (
    <div
      className="pointer-events-none absolute select-none"
      style={{
        left: x, top: startY, fontSize: size,
        animation: `streaker-fall-${dir} ${duration}ms ${delay}ms ease-in forwards`,
        opacity: 0,
      }}
    >
      {emoji}
    </div>
  );
}

// ── Mobile: fast dash with trailing guards ─────────────────────────────────
const MOBILE_GUARD_COUNT    = 4;
const MOBILE_STREAKER_SPEED = 500;
const MOBILE_GUARD_SPEED    = 380;

function MobileStreaker({ triggerKey }) {
  const rafRef   = useRef(null);
  const dotsRef  = useRef([]);
  const stateRef = useRef(null);
  const [confetti, setConfetti] = useState(null);

  const fieldEl   = typeof document !== "undefined" ? document.querySelector(FIELD_SELECTOR) : null;
  const fieldRect = fieldEl ? fieldEl.getBoundingClientRect() : null;

  useEffect(() => {
    if (!triggerKey || !fieldRect) return;
    setConfetti(null);
    const fromLeft = Math.random() < 0.5;
    const laneY    = randRange(fieldRect.height * 0.2, fieldRect.height * 0.75);
    const startX   = fromLeft ? -16 : fieldRect.width + 16;
    const dirX     = fromLeft ? 1 : -1;

    stateRef.current = {
      streakerX: startX, dirX, laneY,
      guards: Array.from({ length: MOBILE_GUARD_COUNT }, (_, i) => ({
        x: startX - dirX * (32 + i * 22),
        delay: i * 50,
      })),
      startTime: performance.now(),
      done: false,
    };

    const spawnMobileConfetti = (type, rect) => {
      const emoji = type === "clap" ? "👏" : "👎";
      const count = type === "clap" ? 14 : 10;
      const fromBottom = type === "clap";
      const particles = Array.from({ length: count }, (_, i) => ({
        id: i, emoji,
        dir:      fromBottom ? "up" : "down",
        x:        randRange(4, rect.width - 4),
        startY:   fromBottom ? rect.height + 10 : -30,
        delay:    randInt(0, 400),
        duration: randInt(700, 1100),
        size:     "1rem",
      }));
      setConfetti({ particles, rect });
      setTimeout(() => setConfetti(null), CONFETTI_DURATION_MS + 600);
    };

    let alive = true;
    const tick = (now) => {
      const s = stateRef.current;
      if (!s || s.done || !alive) return;
      const dt      = Math.min(0.05, (now - (s.lastTick || now)) / 1000);
      s.lastTick    = now;
      const elapsed = now - s.startTime;

      s.streakerX += dirX * MOBILE_STREAKER_SPEED * dt;
      s.guards.forEach(g => { if (elapsed >= g.delay) g.x += dirX * MOBILE_GUARD_SPEED * dt; });

      const els = dotsRef.current;
      if (els[0]) els[0].style.transform = `translate(${s.streakerX}px, ${laneY}px)`;
      s.guards.forEach((g, i) => {
        if (els[i + 1]) els[i + 1].style.transform = `translate(${g.x}px, ${laneY + randRange(-3, 3)}px)`;
      });

      const exited = (dirX === 1 && s.streakerX > fieldRect.width + 20)
                  || (dirX === -1 && s.streakerX < -20);
      if (exited || elapsed > 2500) {
        s.done = true;
        // Mobile run is always quick (<3s) — thumbs down
        spawnMobileConfetti("thumbs", fieldRect);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      alive = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [triggerKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!fieldRect) return null;
  return (
    <>
      {confetti && (
        <div className="pointer-events-none fixed overflow-hidden"
          style={{ top: fieldRect.top, left: fieldRect.left, width: fieldRect.width, height: fieldRect.height, zIndex: 55 }}>
          {confetti.particles.map(p => <ConfettiParticle key={p.id} {...p} />)}
        </div>
      )}
      <div className="pointer-events-none fixed z-50 overflow-hidden"
        style={{ top: fieldRect.top, left: fieldRect.left, width: fieldRect.width, height: fieldRect.height }}>
        <div ref={el => { dotsRef.current[0] = el; }}
          style={{ position: "absolute", top: 0, left: 0, width: 14, height: 14, willChange: "transform", transform: "translate(-20px,-7px)" }}
          className="rounded-full bg-amber-100" />
        {Array.from({ length: MOBILE_GUARD_COUNT }, (_, i) => (
          <div key={i} ref={el => { dotsRef.current[i + 1] = el; }}
            style={{ position: "absolute", top: 0, left: 0, width: 10, height: 10, willChange: "transform", transform: "translate(-30px,-5px)" }}
            className="rounded-full bg-sky-300/95" />
        ))}
      </div>
    </>
  );
}

// ── Main desktop component ─────────────────────────────────────────────────
export default function StreakerEffect({ triggerKey }) {
  const [run, setRun]           = useState(null);
  const [confetti, setConfetti] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [speechBubble, setSpeechBubble] = useState(null);
  const streakerElRef   = useRef(null);
  const guardElsRef     = useRef([]);
  const cancelledRef    = useRef(false);
  const speechTimerRef  = useRef(null);
  // [BUTT-DOT] refs — delete these two lines to revert
  const buttDot1Ref   = useRef(null);
  const buttDot2Ref   = useRef(null);
  const stateRef      = useRef(null);
  const rafRef        = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Initialise a new run when triggerKey changes
  useEffect(() => {
    if (!triggerKey || isMobile) return;
    const fieldEl = document.querySelector(FIELD_SELECTOR);
    if (!fieldEl) return;
    const fieldRect = fieldEl.getBoundingClientRect();
    if (fieldRect.width < 40 || fieldRect.height < 40) return;

    cancelledRef.current = false;
    setConfetti(null);

    const exitEdge = randInt(0, 3);
    const loopDir  = Math.random() < 0.5 ? 1 : -1;

    const entryEdge = randInt(0, 3);
    let startX, startY, initVx, initVy;
    if (entryEdge === 0)      { startX = randRange(0.3, 0.7) * fieldRect.width;  startY = -24;                  initVx = 0;   initVy = 65; }
    else if (entryEdge === 1) { startX = fieldRect.width + 24;                   startY = randRange(0.3, 0.7) * fieldRect.height; initVx = -65; initVy = 0; }
    else if (entryEdge === 2) { startX = randRange(0.3, 0.7) * fieldRect.width;  startY = fieldRect.height + 24; initVx = 0;   initVy = -65; }
    else                      { startX = -24;                                     startY = randRange(0.3, 0.7) * fieldRect.height; initVx = 65;  initVy = 0; }

    const guards = Array.from({ length: NUM_GUARDS }, (_, i) => {
      const edge = i % 4;
      let x, y;
      if (edge === 0)      { x = randRange(0.1, 0.9) * fieldRect.width;  y = -20; }
      else if (edge === 1) { x = fieldRect.width + 20;                   y = randRange(0.1, 0.9) * fieldRect.height; }
      else if (edge === 2) { x = randRange(0.1, 0.9) * fieldRect.width;  y = fieldRect.height + 20; }
      else                  { x = -20;                                    y = randRange(0.1, 0.9) * fieldRect.height; }
      return { x, y, vx: 0, vy: 0, spawnDelayMs: randInt(0, 200) };
    });

    const rect = { top: fieldRect.top, left: fieldRect.left, width: fieldRect.width, height: fieldRect.height };
    // Pre-decide outcome: 10% escape, 90% caught.
    // Physics enforces this via speed caps (lucky: streaker 290 vs guards 195 — escapes;
    // normal: streaker 120 vs guards 195 — gets caught).
    const willEscape = Math.random() < 0.10;
    stateRef.current = {
      streaker: { x: startX, y: startY, vx: initVx, vy: initVy, headingAngle: Math.atan2(initVy, initVx) },
      guards,
      phase: "loop",   // "loop" | "escape" | "caught"
      exitEdge,
      loopDir,
      willEscape,
      caughtX: 0, caughtY: 0,
      startTime: performance.now(),
      lastTick:  performance.now(),
      done: false,
      rect,
    };
    setRun({ key: triggerKey, rect, exitEdge });
  }, [triggerKey, isMobile]);

  // Physics loop
  useEffect(() => {
    if (!run) return;
    let alive = true;
    const fieldRect = run.rect;

    const edgeTarget = (edge) => [
      { x: fieldRect.width / 2,   y: -12 },
      { x: fieldRect.width + 12,  y: fieldRect.height / 2 },
      { x: fieldRect.width / 2,   y: fieldRect.height + 12 },
      { x: -12,                   y: fieldRect.height / 2 },
    ][edge];

    const spawnConfetti = (type) => {
      const emoji  = type === "clap" ? "👏" : "👎";
      const count  = type === "clap" ? CONFETTI_COUNT : THUMBS_COUNT;
      const sizes = ["1rem", "1.2rem", "1.4rem", "1rem", "1.1rem"];
      // Clap rises from bottom, thumbs falls from top.
      const fromBottom = type === "clap";
      const particles = Array.from({ length: count }, (_, i) => ({
        id: i, emoji,
        dir:      fromBottom ? "up" : "down",
        x:        randRange(4, fieldRect.width - 4),
        startY:   fromBottom ? fieldRect.height + 10 : -30,
        delay:    randInt(0, 700),
        duration: randInt(800, 1400),
        size:     sizes[i % sizes.length],
      }));
      setConfetti({ particles, rect: fieldRect });
      setTimeout(() => { if (alive) setConfetti(null); }, CONFETTI_DURATION_MS + 900);
    };

    const tick = (now) => {
      if (!alive || cancelledRef.current) return;
      const s = stateRef.current;
      if (!s) return;
      const dt      = Math.min(0.05, (now - s.lastTick) / 1000);
      s.lastTick    = now;
      const elapsed = now - s.startTime;

      // Phase gate: loop → escape
      if (s.phase === "loop" && elapsed >= LOOP_PHASE_MS) s.phase = "escape";

      const st = s.streaker;
      // Computed outside the phase guard so DOM-write code (butt-dots) always has it.
      // In "caught" phase vx/vy are zeroed, so vmag === 0 → no jiggle.
      const vmag = Math.hypot(st.vx, st.vy);

      if (s.phase !== "caught") {
        const maxSpeed  = s.phase === "escape"
          ? (s.willEscape ? ESCAPE_SPEED_LUCKY : ESCAPE_SPEED)
          : STREAKER_SPEED;
        const obstacles = snapshotObstacles(fieldRect);

        // Heading update
        if (s.phase === "loop") {
          // Circular turn + light wobble
          st.headingAngle += s.loopDir * LOOP_TURN_RATE * dt + randRange(-0.25, 0.25) * dt;
        } else {
          st.headingAngle += randRange(-0.5, 0.5) * dt;
        }

        let ax = Math.cos(st.headingAngle) * STREAKER_ACCEL;
        let ay = Math.sin(st.headingAngle) * STREAKER_ACCEL;

        // Avoid obstacles
        for (const o of obstacles) {
          const dx = st.x - o.cx; const dy = st.y - o.cy;
          const dist = Math.hypot(dx, dy) || 0.001;
          if (dist < o.radius * 2.4) {
            const f = OBSTACLE_REPEL / (dist * dist);
            ax += (dx / dist) * f; ay += (dy / dist) * f;
          }
        }

        // Avoid guards
        for (const g of s.guards) {
          if (elapsed < g.spawnDelayMs) continue;
          const dx = st.x - g.x; const dy = st.y - g.y;
          const dist = Math.hypot(dx, dy) || 0.001;
          if (dist < 115) {
            const f = GUARD_REPEL / (dist * dist);
            ax += (dx / dist) * f; ay += (dy / dist) * f;
          }
        }

        if (s.phase === "loop") {
          // Boundary bounce — keep streaker inside during loops
          if (st.x < BOUNDARY_PAD)                   ax += BOUNDARY_REPEL * (1 - st.x / BOUNDARY_PAD);
          if (st.x > fieldRect.width - BOUNDARY_PAD) ax -= BOUNDARY_REPEL * (1 - (fieldRect.width  - st.x) / BOUNDARY_PAD);
          if (st.y < BOUNDARY_PAD)                   ay += BOUNDARY_REPEL * (1 - st.y / BOUNDARY_PAD);
          if (st.y > fieldRect.height - BOUNDARY_PAD) ay -= BOUNDARY_REPEL * (1 - (fieldRect.height - st.y) / BOUNDARY_PAD);
        } else {
          // Escape bias
          const tgt  = edgeTarget(run.exitEdge);
          const dx   = tgt.x - st.x; const dy = tgt.y - st.y;
          const dist = Math.hypot(dx, dy) || 0.001;
          ax += (dx / dist) * ESCAPE_BIAS;
          ay += (dy / dist) * ESCAPE_BIAS;
        }

        st.vx += ax * dt; st.vy += ay * dt;
        st.vx *= 0.97;    st.vy *= 0.97;
        [st.vx, st.vy] = clampMag(st.vx, st.vy, maxSpeed);
        st.x += st.vx * dt; st.y += st.vy * dt;
        if (vmag > 4) st.headingAngle = Math.atan2(st.vy, st.vx);

        // Catch check — one touch = caught (only while streaker is on screen)
        const streakerOnScreen = st.x > 0 && st.x < fieldRect.width && st.y > 0 && st.y < fieldRect.height;
        for (const g of s.guards) {
          if (elapsed < g.spawnDelayMs) continue;
          if (streakerOnScreen && Math.hypot(st.x - g.x, st.y - g.y) < CATCH_DIST && !s.done) {
            s.phase   = "caught";
            s.caughtX = st.x;
            s.caughtY = st.y;
            st.vx = 0; st.vy = 0;
            s.done = true;
            spawnConfetti("thumbs");
            setTimeout(() => { if (alive) setRun(null); }, PILE_HOLD_MS);
            break;
          }
        }

        // Escape check (escape phase only)
        if (s.phase === "escape" && !s.done) {
          const edge   = run.exitEdge;
          const margin = 10;
          const escaped =
            (edge === 0 && st.y < -margin) ||
            (edge === 1 && st.x > fieldRect.width  + margin) ||
            (edge === 2 && st.y > fieldRect.height + margin) ||
            (edge === 3 && st.x < -margin);
          if (escaped) {
            s.done = true;
            // Only clap if the run lasted ≥ 3 s — a quick escape doesn't impress.
            spawnConfetti(elapsed >= 3000 ? "clap" : "thumbs");
            // Keep the loop alive so the dot continues off screen naturally;
            // only remove the container once it's well clear of the viewport.
            setTimeout(() => { if (alive) setRun(null); }, 750);
          }
        }
      }

      // Guards: chase streaker, pile on caught position
      for (let i = 0; i < s.guards.length; i++) {
        const g = s.guards[i];
        if (elapsed < g.spawnDelayMs) continue;

        const targetX = s.phase === "caught" ? s.caughtX : st.x;
        const targetY = s.phase === "caught" ? s.caughtY : st.y;
        const dx      = targetX - g.x;
        const dy      = targetY - g.y;
        const dist    = Math.hypot(dx, dy) || 0.001;
        const accel   = s.phase === "caught" ? GUARD_ACCEL * 2.5 : GUARD_ACCEL;
        const top     = s.phase === "caught" ? GUARD_PILE_SPEED : GUARD_SPEED;

        g.vx += (dx / dist) * accel * dt;
        g.vy += (dy / dist) * accel * dt;

        // Guard-guard spread when piling so they clump visibly instead of overlapping perfectly
        if (s.phase === "caught") {
          for (let j = 0; j < s.guards.length; j++) {
            if (i === j) continue;
            const gdx  = g.x - s.guards[j].x;
            const gdy  = g.y - s.guards[j].y;
            const gdist = Math.hypot(gdx, gdy) || 0.001;
            if (gdist < 13) {
              const push = 500 / (gdist * gdist);
              g.vx += (gdx / gdist) * push * dt;
              g.vy += (gdy / gdist) * push * dt;
            }
          }
        }

        g.vx *= 0.96; g.vy *= 0.96;
        [g.vx, g.vy] = clampMag(g.vx, g.vy, top);
        g.x += g.vx * dt; g.y += g.vy * dt;
      }

      // DOM writes (rAF path — direct style mutation is correct here)
      if (streakerElRef.current) {
        streakerElRef.current.style.transform = `translate(${st.x - 7}px, ${st.y - 7}px)`;
      }
      // [BUTT-DOT] position two trailing dots opposite to heading — delete this block to revert
      if (buttDot1Ref.current && buttDot2Ref.current) {
        const bx = -Math.cos(st.headingAngle);
        const by = -Math.sin(st.headingAngle);
        const px = -Math.sin(st.headingAngle);
        const py =  Math.cos(st.headingAngle);
        const cx1 = st.x + bx * BUTT_DOT_BACK_DIST + px * BUTT_DOT_SPREAD - BUTT_DOT_RADIUS;
        const cy1 = st.y + by * BUTT_DOT_BACK_DIST + py * BUTT_DOT_SPREAD - BUTT_DOT_RADIUS;
        const cx2 = st.x + bx * BUTT_DOT_BACK_DIST - px * BUTT_DOT_SPREAD - BUTT_DOT_RADIUS;
        const cy2 = st.y + by * BUTT_DOT_BACK_DIST - py * BUTT_DOT_SPREAD - BUTT_DOT_RADIUS;
        // Speed-driven jiggle: freq and amp both derived from current velocity.
        // At zero speed: wave = sin(0) = 0 → s1 = s2 = 1.0 (same size, no pulse).
        // Slow: low freq, large amp (wide lazy waggle).
        // Fast: high freq, small amp (tight rapid flicker).
        const speedRatio = Math.min(vmag / BUTT_JIGGLE_SPEED_REF, 1);
        const liveFreq   = vmag < 1 ? 0
          : BUTT_JIGGLE_FREQ_SLOW + speedRatio * (BUTT_JIGGLE_FREQ_MAX - BUTT_JIGGLE_FREQ_SLOW);
        const liveAmp    = vmag < 1 ? 0
          : BUTT_JIGGLE_AMP_SLOW + speedRatio * (BUTT_JIGGLE_AMP_FAST - BUTT_JIGGLE_AMP_SLOW);
        const wave = Math.sin(2 * Math.PI * liveFreq * elapsed * 0.001);
        const s1 = 1 - liveAmp * Math.max(0,  wave);
        const s2 = 1 - liveAmp * Math.max(0, -wave);
        buttDot1Ref.current.style.transform = `translate(${cx1}px, ${cy1}px) scale(${s1})`;
        buttDot2Ref.current.style.transform = `translate(${cx2}px, ${cy2}px) scale(${s2})`;
      }
      // [/BUTT-DOT]
      for (let i = 0; i < s.guards.length; i++) {
        const el = guardElsRef.current[i];
        if (el) el.style.transform = `translate(${s.guards[i].x - 5}px, ${s.guards[i].y - 5}px)`;
      }

      // Hard timeout — treat as escape
      if (elapsed >= MAX_RUN_MS && !s.done) {
        s.done = true;
        spawnConfetti("clap");
        setTimeout(() => { if (alive) setRun(null); }, 300);
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      alive = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [run]); // eslint-disable-line react-hooks/exhaustive-deps

  const keyframes = (
    <style>{`
      @keyframes streaker-fall-down {
        0%   { opacity: 0; transform: translateY(0)     rotate(0deg); }
        10%  { opacity: 1; }
        90%  { opacity: 1; }
        100% { opacity: 0; transform: translateY(120px)  rotate(22deg); }
      }
      @keyframes streaker-fall-up {
        0%   { opacity: 0; transform: translateY(0)     rotate(0deg); }
        10%  { opacity: 1; }
        90%  { opacity: 1; }
        100% { opacity: 0; transform: translateY(-120px) rotate(-22deg); }
      }
    `}</style>
  );

  if (isMobile) {
    if (!triggerKey) return null;
    return <>{keyframes}<MobileStreaker triggerKey={triggerKey} /></>;
  }

  if (!run && !confetti) return null;

  const clearAll = () => {
    cancelledRef.current = true;
    clearTimeout(speechTimerRef.current);
    setRun(null);
    setConfetti(null);
    setSpeechBubble(null);
  };

  const handleClick = () => {
    const s = stateRef.current;
    if (s) {
      setSpeechBubble({ x: s.streaker.x, y: s.streaker.y });
      speechTimerRef.current = setTimeout(clearAll, 1400);
    } else {
      clearAll();
    }
  };

  const cRect = confetti?.rect ?? run?.rect;

  return (
    <>
      {keyframes}

      {confetti && cRect && (
        <div className="pointer-events-none fixed overflow-hidden"
          style={{ top: cRect.top, left: cRect.left, width: cRect.width, height: cRect.height, zIndex: 55 }}>
          {confetti.particles.map(p => <ConfettiParticle key={p.id} {...p} />)}
        </div>
      )}

      {speechBubble && run && (
        <div className="pointer-events-none fixed z-[60]" style={{
          top:  run.rect.top  + speechBubble.y - 46,
          left: run.rect.left + speechBubble.x - 44,
        }}>
          <div className="relative bg-white text-black text-[11px] font-bold px-2 py-1 rounded-xl border border-gray-300 shadow whitespace-nowrap">
            Eat the rich!
            <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-0 h-0"
              style={{ borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "6px solid white" }} />
          </div>
        </div>
      )}

      {run && (
        <div className="pointer-events-none fixed z-50 overflow-hidden"
          style={{ top: run.rect.top, left: run.rect.left, width: run.rect.width, height: run.rect.height }}>
          {/* [BUTT-DOT] rendered before streaker so main dot layers on top — delete to revert */}
          <div ref={buttDot1Ref}
            style={{ position: "absolute", top: 0, left: 0, width: BUTT_DOT_SIZE_PX, height: BUTT_DOT_SIZE_PX, willChange: "transform", transform: "translate(-200px,-200px)" }}
            className="rounded-full bg-amber-100 pointer-events-none" />
          <div ref={buttDot2Ref}
            style={{ position: "absolute", top: 0, left: 0, width: BUTT_DOT_SIZE_PX, height: BUTT_DOT_SIZE_PX, willChange: "transform", transform: "translate(-200px,-200px)" }}
            className="rounded-full bg-amber-100 pointer-events-none" />
          {/* [/BUTT-DOT] */}
          <button type="button" ref={streakerElRef} onClick={handleClick}
            style={{ position: "absolute", top: 0, left: 0, width: 14, height: 14, willChange: "transform", transform: "translate(-200px,-200px)" }}
            className="pointer-events-auto rounded-full bg-amber-100 cursor-pointer"
            title="Click to end the chase" />
          {Array.from({ length: NUM_GUARDS }, (_, i) => (
            <div key={`g-${run.key}-${i}`}
              ref={el => { guardElsRef.current[i] = el; }}
              style={{ position: "absolute", top: 0, left: 0, width: 10, height: 10, willChange: "transform", transform: "translate(-200px,-200px)" }}
              className="rounded-full bg-sky-300/95" />
          ))}
        </div>
      )}
    </>
  );
}
